import React from "react";
import Footer from "../components/Footer";
import HeroPages from "../components/HeroPages";
import { useBookingContext } from "../context/BookingContext";
import { capitalFirstLetter, currencyFormat, getCarDisplayPrice } from "../utils";
import { Link, useNavigate } from "react-router-dom";
import "react-responsive-modal/styles.css";
import PlaceholderImg from "../images/components/placeholder.jpg";
import useFetch from "../hooks/useFetch";
import moment from "moment";
import Loading from "../components/Loading";
import NoFoundVehicles from "../components/NoFoundVehicles";
import InputBox from "../components/InputBox";
import { useTranslation } from "react-i18next";
import FilterModal from "../components/FilterModal";
import Navbar2 from "../components/Navbar2";
import ProtectionModal from "../components/ProtectionModal";
import { ArrowLeft, ArrowRight } from "lucide-react";

function SelectVehicle2() {
  const { data, setData, setDateUsage } = useBookingContext();
  const [protectionModalOpen, setProtectionModalOpen] = React.useState(false);
  const [filters, setFilters] = React.useState({});
  const [sort, setSort] = React.useState("priceAsc");
  const [filterOpen, setFilterOpen] = React.useState(false);

  const { t } = useTranslation();

  const pickupDate = moment(data?.pickup_date).format("YYYY-MM-DD HH:mm:ss");
  const endDate = moment(data?.dropoff_date).format("YYYY-MM-DD HH:mm:ss");

  const dropoffDate =
    data?.type !== "transfer_service"
      ? endDate
      : moment(pickupDate).set({ hour: 23, minute: 59, second: 59 }).format("YYYY-MM-DD HH:mm:ss");

  let condition = {};
  condition =
    data?.type === "tour"
      ? { hour: parseInt(data.tourPackage) }
      : data?.type === "transfer_service"
        ? { areas: JSON.parse(data?.dropoff_location_json) }
        : {};

  const { items: carsData, isLoading: carLoading } = useFetch({
    module: `cars/available?datetimeFrom=${pickupDate}&datetimeTo=${dropoffDate}&priceType=${data?.type}${
      data?.type !== "self_drive" ? "&condition=" + encodeURIComponent(JSON.stringify(condition)) : ""
    }`,
    enabled: true,
  });

  const { items: pricesData } = useFetch({
    module: "car-prices",
    enabled: true,
  });

  const cars = carsData?.data;
  const prices = pricesData?.data;

  const navigate = useNavigate();

  const _onSelectCar = (values) => {
    setData({ ...data, car: values });
    setDateUsage({ inTown: [], outOfTown: [] });
    navigate("/customer-form");
  };

  const filteredCars = cars?.filter((item) => {
    const values = Object.values(item);
    const searchString = values.join("###").toLowerCase();
    const matchesSearch = filters.search ? searchString.includes(filters.search.toLowerCase()) : true;

    const newPrices = prices?.filter((p) => p.car_id === item?.id);
    const priceByType = newPrices?.find((v) => {
      if (data?.type === "self_drive") {
        return v?.type === "self_drive" && v.condition_type === "default";
      } else if (data?.type === "tour") {
        return v?.type === "tour" && v.condition_type === "default" && v.condition?.hour === Number(data.tourPackage);
      }
      return false;
    });
    const carPrice = priceByType?.price || 0;
    const matchesPrice =
      filters.price?.length === 2 ? carPrice >= filters.price[0] && carPrice <= filters.price[1] : true;

    const matchesPassenger =
      filters.passengers?.length > 0 ? filters.passengers.includes(item.additional_informations?.seat) : true;

    const matchesTransmission =
      filters.transmission?.length > 0 ? filters.transmission.includes(item.transmision) : true;

    const matchesDrivetrain = filters.drivetrain?.length > 0 ? filters.drivetrain.includes(item.drive_system) : true;

    const matchesVehicleType = filters.vehicleType ? item.vehicle_type === filters.vehicleType : true;
    return (
      matchesSearch &&
      matchesPrice &&
      matchesPassenger &&
      matchesTransmission &&
      matchesDrivetrain &&
      matchesVehicleType
    );
  });

  const getCarPrice = (car) => {
    const displayPrice = getCarDisplayPrice(car.id, prices, data?.type);
    return displayPrice.price || 0;
  };

  const sortedCars = React.useMemo(() => {
    if (!filteredCars) return [];

    const carsCopy = [...filteredCars];

    switch (sort) {
      case "priceAsc":
        return carsCopy.sort((a, b) => {
          const priceA = getCarPrice(a);
          const priceB = getCarPrice(b);
          return priceA - priceB;
        });
      case "priceDesc":
        return carsCopy.sort((a, b) => {
          const priceA = getCarPrice(a);
          const priceB = getCarPrice(b);
          return priceB - priceA;
        });
      case "nameAsc":
        return carsCopy.sort((a, b) => a.name.localeCompare(b.name));
      case "nameDesc":
        return carsCopy.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return carsCopy;
    }
  }, [filteredCars, sort, prices, data]);

  React.useEffect(() => {
    if (!data) {
      navigate("/");
    }
  }, [data]);

  React.useEffect(() => {
    window.addEventListener("error", (e) => {
      if (e.message === "ResizeObserver loop limit exceeded") {
        const resizeObserverErrDiv = document.getElementById("webpack-dev-server-client-overlay-div");
        const resizeObserverErr = document.getElementById("webpack-dev-server-client-overlay");
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    });
  }, []);

  const handleApplyFilters = (f) => {
    setFilters(f);
  };

  const handleSortChange = (val) => {
    setSort(val);
  };

  return (
    <>
      <section className="relative min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar2 />
        <HeroPages name={t("select_vehicle")} />
        <InputBox sort={sort} setFilterOpen={() => setFilterOpen(true)} handleSortChange={handleSortChange} />
        <FilterModal open={filterOpen} onClose={() => setFilterOpen(false)} onApply={handleApplyFilters} />
        <div className="container px-4 py-12 mx-auto md:px-8 md:py-32">
          {!sortedCars?.length && !carLoading ? (
            <NoFoundVehicles title={`No cars available on ${data?.pickup_date} - ${data?.dropoff_date}`} />
          ) : null}
          {carLoading ? (
            <Loading />
          ) : (
            <div className="grid grid-cols-1 gap-6 my-24 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {sortedCars?.map((item, idx) => {
                const newPrices = prices?.filter((p) => p.car_id === item?.id);

                // Dapatkan harga yang sesuai untuk ditampilkan
                const displayPrice = getCarDisplayPrice(item.id, prices, data?.type);

                return (
                  <div
                    key={idx}
                    className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100 hover:border-[#f5272b]/30"
                  >
                    {/* Image section - tetap sama */}
                    <div className="relative flex items-center justify-center w-full h-56 overflow-hidden bg-white">
                      <img
                        src={item?.image_urls[0] || PlaceholderImg}
                        alt={item.name}
                        className="object-contain w-full h-full transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-4 right-4">
                        <Link
                          to={`/detail-vehicle`}
                          state={{
                            car: item,
                            prices: newPrices,
                            from: "select-vehicle",
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm hover:bg-[#f5272b] text-slate-800 hover:text-white rounded-md shadow-lg transition-all duration-300 text-sm font-semibold"
                        >
                          <i className="fa-solid fa-circle-info"></i>
                          <span>{t("more_info")}</span>
                        </Link>
                      </div>
                    </div>

                    <div className="p-6 space-y-5">
                      <div>
                        <h3 className="mb-1 text-2xl font-bold text-slate-900">{capitalFirstLetter(item.name)}</h3>
                      </div>

                      {/* Updated Price Display Section */}
                      <div className="p-4 border rounded-lg bg-gradient-to-r from-slate-50 to-transparent border-slate-200">
                        {displayPrice.price > 0 ? (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-slate-600">
                              {displayPrice.serviceType === "self_drive" && t("daily_rate_from")}
                              {displayPrice.serviceType === "tour" && `${t("tour_package")} `}
                              {displayPrice.serviceType === "transfer_service" && t("airport_transfer_from")}
                            </p>
                            <p className="text-3xl font-bold text-[#f5272b]">{currencyFormat(displayPrice.price)}</p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-slate-600">{t("contact_for_price")}</p>
                            <p className="text-lg text-slate-500">{t("price_varies_by_service")}</p>
                          </div>
                        )}

                        <div className="pt-3 mt-3 ">
                          <button
                            onClick={() => setProtectionModalOpen(true)}
                            className="flex items-start w-full gap-2 p-3 text-left transition-all duration-200 border border-green-100 rounded-lg bg-green-100/50"
                          >
                            <i className="fa-solid fa-circle-check text-green-600 mt-0.5 flex-shrink-0"></i>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-semibold text-slate-900">{t("includes")}: </span>
                                <span className="text-[#f5272b] font-medium text-sm">{t("basic_protection")}</span>
                                <i className="flex-shrink-0 ml-1 text-xs fa-solid fa-circle-info text-slate-400"></i>
                              </div>

                              <div className="mt-0.5">
                                <span className="text-xs text-slate-600">{t("km_unlimited")}</span>
                              </div>
                              <div className="mt-0.5 flex items-center text-blue-600 gap-1">
                                <span className="text-xs">{t("see_more_details")}</span>
                                <ArrowRight className="w-4 h-4" />
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Info Cards - tetap sama */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-tag text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Brand</p>
                            <p className="text-sm font-semibold truncate text-slate-900">
                              {capitalFirstLetter(item.brands)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-gears text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Trans.</p>
                            <p className="text-sm font-semibold truncate text-slate-900">
                              {capitalFirstLetter(item.transmision)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-car text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Drive</p>
                            <p className="text-sm font-semibold truncate text-slate-900">{item?.drive_system}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 border rounded-md bg-slate-50 border-slate-200">
                          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                            <i className="fa-solid fa-users text-[#f5272b]"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500">Seats</p>
                            <p className="text-sm font-semibold text-slate-900">
                              {item?.additional_informations?.seat} {t("passenger")}
                            </p>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => _onSelectCar(item, newPrices)}
                        className="w-full px-6 py-4 bg-[#f5272b] hover:bg-red-700 text-white font-bold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                      >
                        <span className="flex items-center justify-center gap-3">
                          {t("book_ride").toUpperCase()}
                          <i className="transition-transform fa-solid fa-arrow-right group-hover:translate-x-1"></i>
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <Footer />

        {/* Protection Details Modal */}
        <ProtectionModal open={protectionModalOpen} onClose={() => setProtectionModalOpen(false)} />
      </section>
    </>
  );
}

export default SelectVehicle2;
